import {
  AltRoute,
  Check,
  Close,
  DeleteOutlined,
  Draw,
  EditRoad,
  Undo,
  WrongLocation,
} from "@mui/icons-material";
import { FC } from "react";
import { useTranslation } from "react-i18next";

import { PolylineToolButton } from "../../parts/polyline-tool-button";
import { POLYLINE_TOOL_COLOR } from "../../parts/polyline-tool-button.constants";
import { PolylineToolbarContainer } from "../../parts/polyline-tool-button.styled";
import { IUseMapLibrePolylineEditResult } from "./use-maplibre-polyline-edit.models";

/**
 * Floating draw/edit toolbar for the course polyline. Purely presentational: every button's visibility and
 * handler comes from `useMapLibrePolylineEdit`, so which buttons exist in which mode is readable in one place.
 */
export const MapLibrePolylineToolbar: FC<{
  state: IUseMapLibrePolylineEditResult;
}> = ({ state }) => {
  const { t } = useTranslation();
  const {
    toolbarRef,
    isDrawing,
    isEditing,
    isDeletePointMode,
    drawingPoints,
    finalPositions,
    canFinishDraw,
    isSnapButtonVisible,
    isRemoveManualPolylineVisible,
    isManualPolylineEdit,
  } = state;
  const isIdle = !isDrawing && !isEditing;

  return (
    <PolylineToolbarContainer ref={toolbarRef}>
      {isIdle && (
        <PolylineToolButton
          icon={<Draw />}
          label={t("ui.DRAW_POLYLINE")}
          color={POLYLINE_TOOL_COLOR.draw}
          onClick={state.handleDrawStart}
          className="start-draw-polyline-button"
        />
      )}

      {isIdle && finalPositions.length > 0 && (
        <PolylineToolButton
          icon={<EditRoad />}
          label={t("ui.EDIT_POLYLINE")}
          color={
            isManualPolylineEdit
              ? POLYLINE_TOOL_COLOR.manualPolyline
              : POLYLINE_TOOL_COLOR.edit
          }
          isHighlighted={isManualPolylineEdit}
          onClick={state.handleEditStart}
          className="edit-polyline-button"
        />
      )}

      {isDrawing && (
        <>
          {drawingPoints.length > 0 && (
            <PolylineToolButton
              icon={<Undo />}
              label={t("ui.UNDO_LAST_POINT")}
              color={POLYLINE_TOOL_COLOR.undo}
              onClick={state.handleUndoLastPoint}
              className="undo-draw-polyline-button"
            />
          )}
          {canFinishDraw && (
            <PolylineToolButton
              icon={<Check />}
              label={t("ui.SAVE_POLYLINE")}
              color={POLYLINE_TOOL_COLOR.confirm}
              onClick={state.handleDrawEnd}
              className="save-draw-polyline-button"
            />
          )}
          <PolylineToolButton
            icon={<Close />}
            label={t("ui.CANCEL_POLYLINE_EDIT")}
            color={POLYLINE_TOOL_COLOR.cancel}
            onClick={state.handleDrawCancel}
            className="cancel-draw-polyline-button"
          />
        </>
      )}

      {isEditing && (
        <>
          <PolylineToolButton
            icon={<Check />}
            label={t("ui.SAVE_POLYLINE")}
            color={POLYLINE_TOOL_COLOR.confirm}
            onClick={state.handleEditSave}
            className="save-draw-polyline-button"
          />
          <PolylineToolButton
            icon={<WrongLocation />}
            label={t("ui.DELETE_POINT")}
            color={POLYLINE_TOOL_COLOR.deletePoint}
            isActive={isDeletePointMode}
            onClick={state.handleToggleDeletePointMode}
            className="delete-polyline-point-button"
          />
          <PolylineToolButton
            icon={<Close />}
            label={t("ui.CANCEL_POLYLINE_EDIT")}
            color={POLYLINE_TOOL_COLOR.cancel}
            onClick={state.handleEditCancel}
            className="cancel-draw-polyline-button"
          />
        </>
      )}

      {isSnapButtonVisible && (
        <PolylineToolButton
          icon={<AltRoute />}
          label={t("ui.SNAP_ROUTE")}
          color={POLYLINE_TOOL_COLOR.snap}
          onClick={state.handleSnapRouteClick}
          className="snap-draw-polyline-button"
        />
      )}

      {isRemoveManualPolylineVisible && (
        <PolylineToolButton
          icon={<DeleteOutlined />}
          label={t("ui.REMOVE_MANUAL_POLYLINE")}
          color={POLYLINE_TOOL_COLOR.remove}
          onClick={state.handleRemoveManualPolyline}
          className="remove-manual-polyline-button"
        />
      )}
    </PolylineToolbarContainer>
  );
};
